import React, { useState, useRef, type JSX } from 'react';
import { Calendar, type CalendarProps } from 'primereact/calendar';
import styles from './Datepicker.module.scss';
import CalendarImage from '../../../assets/Icons/Library/Calendar_add_outline.svg';

/**
 * CustomDatepickerProps defines the properties for the Datepicker component.
 */
export interface CustomDatePickerProps {
  datePickerType: 'Single' | 'Range';
  states: 'Default' | 'Active' | 'Selected' | 'Disabled';
  label?: string;
  value: Date | [Date | null, Date | null] | null;
  onChange: (value: Date | [Date | null, Date | null] | null) => void;
  placeholder?: string;
  calendarProps?: Partial<CalendarProps>;
}

type DatePickerValue = CustomDatePickerProps['value'];

/**
 * Custom Datepicker component using PrimeReact's Calendar. It supports both single and range date selection, with styling that reflects the selected state.
 *
 * ## Import path
 * ```tsx
 * import { DatePicker } from '@kpmg-global-audit-emu/react-ui-kit/components/DatePicker';
 * ```
 * DatePicker component supporting single or range selection with optional states.
 * @param {object} props - Component props.
 * @param {string} props.label - Label for the date picker.
 * @param {'Single' | 'Range'} props.datePickerType - Type of date picker: single date or date range.
 * @param {string[]} [props.states] - Optional states to display (e.g., for status-based highlighting).
 * @param {string} [props.placeholder] - Placeholder text for the input field.
 * @param {Date | [Date | null, Date | null] | null} props.value - Selected date or date range.
 * @param {(value: Date | [Date | null, Date | null] | null) => void} props.onChange - Callback function when the date or date range changes.
 * @param {Partial<CalendarProps>} [props.calendarProps] - Additional props to pass to the underlying PrimeReact Calendar component.
 * @returns {JSX.Element} Rendered DatePicker component.
 */
export const DatePicker: React.FC<CustomDatePickerProps> = ({
  datePickerType,
  states,
  label = 'Date',
  value,
  onChange,
  placeholder = 'Select Date',
  calendarProps,
}): JSX.Element => {
  const [date, setDate] = useState<DatePickerValue>(value);
  const calendarRef = useRef<Calendar>(null);

  const isRange = datePickerType === 'Range';
  const isDisabled = states === 'Disabled';
  const stateClass = states === 'Disabled' ? styles['label_disabled'] : '';

  const calendarIcon = () => <img src={CalendarImage} alt="Toggle Calendar" />;

  const handleChange = (e: { value: Date | (Date | null)[] | null | undefined }) => {
    const selected: DatePickerValue = Array.isArray(e.value)
      ? [e.value[0] ?? null, e.value[1] ?? null]
      : e.value ?? null;

    setDate(selected);
    onChange?.(selected);

    if (isRange) {
      // For range: auto-close only when both start and end dates are selected
      if (Array.isArray(selected) && selected[0] !== null && selected[1] !== null) {
        calendarRef.current?.hide();
      }
    } else {
      // For single: auto-close immediately after a date is picked
      calendarRef.current?.hide();
    }
  };

  return (
    <div>
      <label htmlFor="datePicker" className={`${styles['datepicker-label']} ${stateClass}`}>
        <span>{label}</span>
        <span className={styles.mandatory}> * </span>
      </label>
      <div
        className={`${styles['custom-datepicker']} ${styles.default} ${styles[states.toLowerCase()]} ${isRange ? styles['rangemain'] : ''
          }`}
      >
        <Calendar
          ref={calendarRef}
          value={date}
          onChange={handleChange}
          selectionMode={isRange ? 'range' : 'single'}
          placeholder={placeholder}
          dateFormat="mm/dd/yy"
          showIcon
          inputId="datePicker"
          disabled={isDisabled}
          appendTo={document.body}
          hideOnDateTimeSelect={false}
          className={[styles.calendar, calendarProps?.className].filter(Boolean).join(' ')}
          icon={calendarIcon}
          {...calendarProps}
        />
      </div>
    </div>
  );
};
